/**
 * vitals.ts — Web Vitals reporting via track().
 *
 * Reports LCP, INP, CLS, TTFB, FCP as `vital` events per §3 / §5.
 * Each vital is enqueued (not immediately flushed) — the normal flush cycle
 * sends them. Deferred until after sign-in like all other events.
 *
 * Contract (§10):
 *   - Uses the web-vitals library (onLCP, onINP, onCLS, onTTFB, onFCP).
 *   - DNT/GPC check happens in enqueueVital before queuing.
 *   - All errors swallowed — telemetry must NEVER affect app behavior.
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { isDntActive } from './dnt';
import { enqueueEvent } from './queue';
import { currentRouteTemplate } from './route';
import type { VitalEvent } from './types';

// ---------------------------------------------------------------------------
// Vital name mapping — web-vitals uses the same names as the contract
// ---------------------------------------------------------------------------

type VitalName = VitalEvent['name'];
type VitalRating = VitalEvent['rating'];

function toVitalName(name: string): VitalName | null {
  if (name === 'LCP' || name === 'INP' || name === 'CLS' || name === 'TTFB' || name === 'FCP') {
    return name as VitalName;
  }
  return null;
}

function toVitalRating(rating: string): VitalRating | null {
  if (rating === 'good' || rating === 'needs-improvement' || rating === 'poor') {
    return rating as VitalRating;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Internal enqueue helper
// ---------------------------------------------------------------------------

function enqueueVital(metric: Metric): void {
  try {
    if (isDntActive()) return;

    const name = toVitalName(metric.name);
    if (!name) return;

    const rating = toVitalRating(metric.rating);
    if (!rating) return;

    // §5: value must be finite f64 ≥ 0
    if (!Number.isFinite(metric.value) || metric.value < 0) return;

    const event: VitalEvent = {
      type: 'vital',
      ts: new Date().toISOString(),
      routeTemplate: currentRouteTemplate(),
      online: typeof navigator !== 'undefined' ? navigator.onLine : true,
      name,
      value: metric.value,
      rating,
    };

    void enqueueEvent(event).catch(() => {
      /* swallow */
    });
  } catch {
    // swallow
  }
}

// ---------------------------------------------------------------------------
// Public — call once at app init
// ---------------------------------------------------------------------------

let _initialised = false;

/**
 * Register Web Vitals reporters. Safe to call multiple times (no-op after first).
 * Must be called from main.tsx or App.tsx (not inside a React component) so
 * metrics are captured before the app fully renders.
 */
export function initVitals(): void {
  if (_initialised) return;
  _initialised = true;

  try {
    onLCP(enqueueVital);
    onINP(enqueueVital);
    onCLS(enqueueVital);
    onTTFB(enqueueVital);
    onFCP(enqueueVital);
  } catch {
    // web-vitals unsupported in this environment (jsdom, old browser) — swallow.
  }
}
